import React, { forwardRef, useState } from "react";
import * as Sentry from "@sentry/react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { ExportAttachmentProps, ExportAttachmentsHandler } from "models/propTypes/revisorPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import ExportAttachmentsView from "./exportAttachmentsView";
import useExportAttachmentsHelper from "./useExportAttachmentsHelper";
import useExportAttachments from "./useExportAttachments";
import { describeFailedExports } from "./exportFailure";
import { useFloatingMessages } from "components/common";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { useDocumentService } from "serviceHooks";

const ExportAttachmentsContainer: React.ForwardRefRenderFunction<ExportAttachmentsHandler, ExportAttachmentProps> = (
  props,
  ref
) => {
  const { getExportAttachmentTypes } = useExportAttachmentsHelper();
  const useExportAttachmentService = useExportAttachments();
  const { enqueueSuccessMessage, enqueueErrorMessage } = useFloatingMessages();
  const { annualAccountsFinancialYear, financialYears } = useSelector((store: AppState) => store.auth.selectedCompany);
  const { getSharedDocumentsForFinancialYear } = useDocumentService();

  const today = DateHelper.today();
  const currentFinancialYear = financialYears?.find((f) => DateHelper.withinIntervall(today, f.start, f.end));
  const [sharedDocuments, setSharedDocuments] = useState<string[]>();

  const previousPeriodText = DateHelper.formatDateRange(
    annualAccountsFinancialYear?.start,
    annualAccountsFinancialYear?.end,
    "-"
  );
  const currentPeriodText = DateHelper.formatDateRange(currentFinancialYear?.start, today, "-");

  React.useEffect(() => {
    if (!annualAccountsFinancialYear) {
      return;
    }

    getSharedDocumentsForFinancialYear(annualAccountsFinancialYear.id).then((result) => {
      const filteredDocuments = result
        ?.filter((d) => d.type !== WintSuperkollAPIModel.DriveFileItemType.Directory && d.title)
        .map((d) => d.title!);
      setSharedDocuments(filteredDocuments);
    });
  }, [annualAccountsFinancialYear]);

  React.useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!annualAccountsFinancialYear) {
        return Promise.reject();
      }

      const exportItemsResult = await useExportAttachmentService.exportAttachments(
        annualAccountsFinancialYear.isAutoAnnualReport,
        { start: annualAccountsFinancialYear.start, end: annualAccountsFinancialYear.end },
        currentFinancialYear ? { start: currentFinancialYear.start, end: today } : undefined,
        sharedDocuments === undefined || sharedDocuments.length > 0
      );

      const failedExports = exportItemsResult.filter((result) => !result.success);
      if (failedExports.length) {
        failedExports.forEach((result) =>
          Sentry.captureMessage("Revisor export failed", {
            level: "warning",
            tags: { source: "revisor-export", part: result.typeKey },
            extra: { reason: result.reason },
          })
        );
        enqueueErrorMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Revisor.ExportAttachmentsFailed, "", {
            failedExports: describeFailedExports(failedExports),
          })
        );
      } else {
        enqueueSuccessMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.QueueExportSuccess)
        );
      }

      props.reload && props.reload();
      return Promise.resolve();
    },
  }));

  return (
    <ExportAttachmentsView
      exportItemTypes={getExportAttachmentTypes(
        previousPeriodText,
        currentPeriodText,
        annualAccountsFinancialYear?.isAutoAnnualReport,
        sharedDocuments
      )}
    />
  );
};

export default forwardRef(ExportAttachmentsContainer);
